import { useEffect, useState } from 'react';

interface UseActiveSectionOptions {
  /**
   * Optional scroll container to observe within. When the docs layout
   * has the article as its own scroll container (desktop: `lg:overflow-y-auto`),
   * pass that element so IntersectionObserver tracks visibility against
   * the article's scrollport rather than the viewport. Below the
   * breakpoint where the article scrolls independently, pass `null` so
   * we fall back to the viewport.
   */
  root?: Element | null;
  /**
   * Top offset (in pixels) for the IntersectionObserver `rootMargin`.
   * Should match the height of any sticky chrome that overlaps the
   * top of the scroll container (e.g. the site navbar). Default: `80`.
   */
  topOffsetPx?: number;
  /**
   * Whether to update the URL hash to the active section as the user
   * scrolls. Uses `history.replaceState` so the browser back button
   * stays clean. Default: `true`.
   */
  syncHash?: boolean;
}

/**
 * Returns the id of the section currently in view among the given ids,
 * using a single `IntersectionObserver` across all of them. Designed
 * to drive the active-link highlight in a docs-style left TOC.
 *
 * The activation band starts `topOffsetPx` below the top of the scroll
 * container (to clear any sticky chrome) and extends down to roughly
 * the upper third of the viewport — so a section is "active" as soon
 * as its heading scrolls past the upper portion of the reader's view,
 * which feels more natural than waiting for the section to occupy the
 * centre.
 *
 * Optionally syncs the URL hash to the active section via
 * `history.replaceState` (default on) — keeps deep links shareable
 * without polluting the back/forward history.
 */
export function useActiveSection(
  ids: readonly string[],
  {
    root = null,
    topOffsetPx = 80,
    syncHash = true,
  }: UseActiveSectionOptions = {}
): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);

  // Depend on a joined string instead of the array identity so
  // consumers can pass an inline array literal without re-running the
  // effect on every render. Extracted to its own variable (rather than
  // computed inline in the dep array) so `react-hooks/exhaustive-deps`
  // can statically check it.
  const idsKey = ids.join('|');

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || idsKey === '') {
      return;
    }

    const elements = idsKey
      .split('|')
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Of the headings currently in the activation band, pick the
        // topmost one. Sorting by `boundingClientRect.top` ascending is
        // what gives the "first heading the reader has reached"
        // semantics rather than "most visible heading", which can flip
        // unpredictably as long sections scroll past short ones.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length === 0) return;

        const id = visible[0].target.id;
        setActiveId((prev) => {
          if (prev === id) return prev;
          if (syncHash && window.location.hash !== `#${id}`) {
            // `replaceState` avoids creating a back-history entry per
            // scroll position — only explicit anchor clicks (handled
            // elsewhere) should push history.
            window.history.replaceState(null, '', `#${id}`);
          }
          return id;
        });
      },
      {
        root: root ?? null,
        // Top margin clears sticky chrome; bottom margin pulls the
        // activation point up so we activate the section as it enters
        // the top third of the scrollport, not when it reaches centre.
        rootMargin: `-${topOffsetPx}px 0px -66% 0px`,
        threshold: 0,
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [idsKey, root, topOffsetPx, syncHash]);

  return activeId;
}

/**
 * Click handler for in-page anchor links that gives smooth scroll
 * regardless of which element is the scroll container, and pushes a
 * real history entry (so the back button takes the user to where they
 * were before clicking the TOC link). Honours the heading's
 * `scroll-margin-top` (set via Tailwind's `scroll-mt-*` utilities) so
 * the heading lands below any sticky chrome.
 *
 * Wire onto each TOC `<a>`:
 *
 *     <a href={href} onClick={(e) => scrollToHash(e, href)}>{label}</a>
 *
 * The handler no-ops on modifier-clicks (cmd/ctrl/shift/middle/right)
 * so users can still open the deep link in a new tab.
 */
export function scrollToHash(
  e: React.MouseEvent<HTMLAnchorElement>,
  href: string
): void {
  if (e.defaultPrevented) return;
  if (e.button !== 0) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

  const id = href.startsWith('#') ? href.slice(1) : href;
  const target = document.getElementById(id);
  if (!target) return;

  e.preventDefault();
  // Respect the OS-level "reduce motion" preference (used by readers
  // with vestibular sensitivity); falling back to an instant jump
  // still lands on the correct anchor and still pushes a history
  // entry below.
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
    block: 'start',
  });
  // `pushState` (not `replace`) so the back button returns the reader
  // to wherever they were before clicking. The scroll-spy effect will
  // call `replaceState` on subsequent scroll updates without piling on
  // history entries.
  if (window.location.hash !== `#${id}`) {
    window.history.pushState(null, '', `#${id}`);
  }
}
